import React, { useState, useEffect } from 'react';
import {
  Search,
  Calendar,
  History as HistoryIcon,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { diaryAPI, handleAPIError } from './api';
import { useTheme } from '@mui/material/styles';

// ---------------------------------------------------------------------
// Restyled to match the rest of the app (PerformancePeriods.js,
// GroupsView.js): one bordered card holding the title, filter bar, table
// and footer count, a plain (no gradient) uppercase table header, and the
// same cell padding/size as those tables.
// ---------------------------------------------------------------------

const HistoryView = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchTerm, setSearchTerm] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');

  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);

  const loadHistoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      const historyResponse = await diaryAPI.history.getHistory();
      const historyItems = historyResponse.results || historyResponse || [];

      const transformedHistory = historyItems.map(item => ({
        id: item.id,
        group: item.group?.name || 'N/A',
        groupId: item.group?.id,
        periodStart: item.period_start,
        periodEnd: item.period_end,
        loansFromOffice: item.approved_loans_from_office || 0,
        advanceFromOffice: item.requested_advance_loans || 0,
        totalExpenses: item.total_expenses || 0,
        officers: item.officers?.map(officer => ({
          name: `${officer.first_name} ${officer.last_name}`.trim() || officer.username,
          avatar: getOfficerInitials(`${officer.first_name} ${officer.last_name}`.trim() || officer.username)
        })) || [],
        status: 'Ended',
        completedAt: item.completed_at,
        completedBy: item.completed_by_name
      }));

      const sortedHistory = transformedHistory.sort((a, b) =>
        new Date(b.completedAt) - new Date(a.completedAt)
      );

      setHistoryData(sortedHistory);

      const groups = [...new Set(sortedHistory.map(item => item.group).filter(group => group !== 'N/A'))];
      setAvailableGroups(groups);

    } catch (err) {
      const errorMessage = handleAPIError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistoryData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `KES ${parseFloat(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  };

  const getOfficerInitials = (name) => {
    return name.split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  const getTransformedOfficers = (officers) => {
    return officers.map(officer => ({
      ...officer,
      avatar: getOfficerInitials(officer.name)
    }));
  };

  const filteredHistory = historyData.filter(item => {
    const matchesSearch = item.group.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = groupFilter === 'all' || item.group === groupFilter;

    let matchesPeriod = true;
    if (periodFilter !== 'all') {
      const periodEndDate = new Date(item.periodEnd);
      const currentDate = new Date();

      switch (periodFilter) {
        case 'thisMonth':
          matchesPeriod = periodEndDate.getMonth() === currentDate.getMonth() &&
                        periodEndDate.getFullYear() === currentDate.getFullYear();
          break;
        case 'lastMonth':
          const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
          matchesPeriod = periodEndDate.getMonth() === lastMonth.getMonth() &&
                        periodEndDate.getFullYear() === lastMonth.getFullYear();
          break;
        case 'last3Months':
          const threeMonthsAgo = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3);
          matchesPeriod = periodEndDate >= threeMonthsAgo;
          break;
        default:
          matchesPeriod = true;
      }
    }

    return matchesSearch && matchesGroup && matchesPeriod;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading meeting history...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>Failed to load meeting history: {error}</p>
        <button
          onClick={loadHistoryData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pb-32">
      {/* History card — title, filter bar, table and footer count all in
         one card, matching PerformancePeriods.js / GroupsView.js. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`flex items-center gap-2 text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <HistoryIcon className="w-5 h-5 text-blue-600" />
            Meeting History
          </h2>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Completed meetings across all groups.
          </p>
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative flex-1 min-w-[220px]">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Groups</option>
            {availableGroups.map(group => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </select>

          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Periods</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="last3Months">Last 3 Months</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Group
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Period Start
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Period End
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Loans from Office
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Advance from Office
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Total Expenses
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Officer(s)
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredHistory.map((item) => (
                <tr key={item.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className={`px-6 py-4 font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {item.group}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      <Calendar className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {formatDate(item.periodStart)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      <Calendar className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {formatDate(item.periodEnd)}
                      </span>
                    </div>
                  </td>
                  <td className={`px-6 py-4 font-semibold ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                    {formatCurrency(item.loansFromOffice)}
                  </td>
                  <td className={`px-6 py-4 font-semibold ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                    {formatCurrency(item.advanceFromOffice)}
                  </td>
                  <td className={`px-6 py-4 font-semibold ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                    {formatCurrency(item.totalExpenses)}
                  </td>
                  <td className="px-6 py-4">
                    {item.officers.length > 0 ? (
                      <div className="flex items-center">
                        <div className="flex items-center -space-x-2">
                          {getTransformedOfficers(item.officers).slice(0, 3).map((officer, idx) => (
                            <div
                              key={idx}
                              title={officer.name}
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ring-2 ${
                                isDark ? 'ring-[#2b323c] bg-blue-900/60 text-blue-200' : 'ring-white bg-blue-100 text-blue-800'
                              }`}
                            >
                              {officer.avatar}
                            </div>
                          ))}
                        </div>
                        {item.officers.length > 3 && (
                          <span className={`ml-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            +{item.officers.length - 3} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredHistory.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No meeting history found</p>
            <p className="text-sm mt-1">Try adjusting your search criteria or filters</p>
          </div>
        )}

        {/* Footer count */}
        <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          Showing {filteredHistory.length} of {historyData.length} completed meetings
        </div>
      </div>
    </div>
  );
};

export default HistoryView;